import { useMemo, useState } from "react";

import { AlertCircle, Github } from "lucide-react";

import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { supabase } from "../lib/supabase";
import {
  normalizeEmail,
  passwordsMatch,
  schemas,
  validateSchema,
} from "../utils/validation";

const getErrorMessage = (error, isSignup) => {
  if (!error) return "An unexpected error occurred. Please try again.";

  const errorCode = error.code || "";
  const errorMsg = error.message || "";

  if (
    errorCode === "user_already_exists" ||
    errorMsg.includes("already registered")
  ) {
    return "An account with this email already exists. Please sign in instead.";
  }

  if (
    errorCode === "email_address_invalid" ||
    errorCode === "validation_failed"
  ) {
    return "Please enter a valid email address.";
  }

  if (
    errorCode === "weak_password" ||
    errorMsg.includes("Password should be")
  ) {
    return "Password is too weak. Use at least 8 characters with uppercase, lowercase, and a number.";
  }

  if (
    errorCode === "invalid_credentials" ||
    errorCode === "invalid_login_credentials"
  ) {
    return "Invalid email or password. Please check your credentials and try again.";
  }

  if (errorCode === "email_not_confirmed") {
    return "Please confirm your email before signing in. Check your inbox for the confirmation link.";
  }

  if (errorCode === "rate_limit" || errorMsg.includes("rate limit")) {
    return "Too many attempts. Please wait a moment and try again.";
  }

  if (errorCode === "network_error" || errorMsg.includes("network")) {
    return "Network error. Please check your internet connection and try again.";
  }

  return (
    errorMsg ||
    (isSignup
      ? "Failed to create account. Please try again."
      : "Failed to sign in. Please try again.")
  );
};

const getFirstSchemaError = (errors) => {
  const [firstKey] = Object.keys(errors || {});
  return firstKey ? errors[firstKey] : null;
};

const AuthModal = ({ isOpen, onClose, showToast }) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const normalizedEmail = useMemo(() => normalizeEmail(email), [email]);

  const clearError = () => {
    if (error) setError(null);
  };

  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setAcceptTerms(false);
    setIsSignup(false);
    setError(null);
  };

  const validateSignup = () => {
    if (!fullName.trim() || fullName.trim().length < 2) {
      return "Please enter your full name (at least 2 characters).";
    }

    const validation = validateSchema(
      {
        email: normalizedEmail,
        password,
        confirmPassword,
      },
      schemas.signup,
    );

    if (!validation.isValid) {
      return (
        getFirstSchemaError(validation.errors) || "Please check your input."
      );
    }

    if (!passwordsMatch(password, confirmPassword)) {
      return "Passwords do not match.";
    }

    if (!acceptTerms) {
      return "Please accept the terms to create your account.";
    }

    return null;
  };

  const handleOAuthLogin = async (provider) => {
    setIsLoading(true);
    clearError();

    try {
      const oauthOptions = {
        redirectTo: `${window.location.origin}/`,
      };

      if (provider === "google") {
        oauthOptions.queryParams = {
          prompt: "select_account",
        };
      }

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: oauthOptions,
      });

      if (oauthError) throw oauthError;
    } catch (oauthError) {
      const friendlyMessage = getErrorMessage(oauthError, false);
      setError(friendlyMessage);
      showToast(friendlyMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isSignup) {
        const signupError = validateSignup();
        if (signupError) {
          setError(signupError);
          showToast(signupError, "error");
          setIsLoading(false);
          return;
        }

        const { data, error: signupRequestError } = await supabase.auth.signUp({
          email: normalizedEmail,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              full_name: fullName.trim(),
            },
          },
        });

        if (signupRequestError) throw signupRequestError;

        if (data?.user && !data?.session) {
          showToast(
            "Account created. Please check your email to confirm your account.",
            "success",
          );
          setPassword("");
          setConfirmPassword("");
          setAcceptTerms(false);
          setIsSignup(false);
          return;
        }

        showToast("Account created successfully. Welcome!", "success");
        onClose();
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: normalizedEmail,
          password,
        });

        if (signInError) throw signInError;

        showToast("Welcome back!", "success");
        onClose();
      }

      resetForm();
    } catch (authError) {
      const friendlyMessage = getErrorMessage(authError, isSignup);
      setError(friendlyMessage);
      showToast(friendlyMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = (e) => {
    e.preventDefault();
    setIsSignup((prev) => !prev);
    setPassword("");
    setConfirmPassword("");
    setAcceptTerms(false);
    setError(null);
  };

  const dialogDescriptionId = "auth-dialog-description";
  const errorId = "auth-error-message";

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          resetForm();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-[420px] bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
        aria-describedby={dialogDescriptionId}
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-800 dark:text-slate-100">
            {isSignup ? "Create Account" : "Sign In"}
          </DialogTitle>
          <DialogDescription id={dialogDescriptionId} className="sr-only">
            {isSignup
              ? "Create a new account using your email and password, or sign up with Google or GitHub."
              : "Sign in to your account using your email and password, or continue with Google or GitHub."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <Button
            variant="outline"
            onClick={() => handleOAuthLogin("github")}
            disabled={isLoading}
            className="w-full gap-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200"
            aria-label="Continue with GitHub"
          >
            <Github size={18} aria-hidden="true" />
            Continue with GitHub
          </Button>

          <Button
            variant="outline"
            onClick={() => handleOAuthLogin("google")}
            disabled={isLoading}
            className="w-full gap-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200"
            aria-label="Continue with Google"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-slate-300 dark:border-slate-600" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white dark:bg-slate-800 px-2 text-slate-500">
              Or continue with email
            </span>
          </div>
        </div>

        {error && (
          <div
            id={errorId}
            className="flex items-start gap-2 p-3 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 rounded-md border border-red-200 dark:border-red-800"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle
              size={16}
              className="mt-0.5 flex-shrink-0"
              aria-hidden="true"
            />
            <span>{error}</span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-4 py-2"
          aria-describedby={error ? errorId : undefined}
        >
          {isSignup && (
            <div className="space-y-2">
              <label
                htmlFor="auth-full-name"
                className="text-sm font-medium text-slate-600 dark:text-slate-300"
              >
                Full name
              </label>
              <Input
                id="auth-full-name"
                name="fullName"
                type="text"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  clearError();
                }}
                placeholder="Jane Doe"
                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"
                required
                autoComplete="name"
                disabled={isLoading}
              />
            </div>
          )}

          <div className="space-y-2">
            <label
              htmlFor="auth-email"
              className="text-sm font-medium text-slate-600 dark:text-slate-300"
            >
              Email
            </label>
            <Input
              id="auth-email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                clearError();
              }}
              placeholder="you@example.com"
              className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"
              required
              autoComplete={isSignup ? "email" : "username email"}
              aria-required="true"
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="auth-password"
              className="text-sm font-medium text-slate-600 dark:text-slate-300"
            >
              Password
            </label>
            <Input
              id="auth-password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                clearError();
              }}
              placeholder="••••••••"
              className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"
              required
              autoComplete={isSignup ? "new-password" : "current-password"}
              minLength={isSignup ? 8 : 6}
              aria-required="true"
              disabled={isLoading}
            />
            {isSignup && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Use at least 8 characters with uppercase, lowercase, and a
                number.
              </p>
            )}
          </div>

          {isSignup && (
            <div className="space-y-2">
              <label
                htmlFor="auth-confirm-password"
                className="text-sm font-medium text-slate-600 dark:text-slate-300"
              >
                Confirm password
              </label>
              <Input
                id="auth-confirm-password"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  clearError();
                }}
                placeholder="••••••••"
                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 dark:text-white dark:placeholder:text-slate-400"
                required
                autoComplete="new-password"
                minLength={8}
                aria-required="true"
                disabled={isLoading}
              />
            </div>
          )}

          {isSignup && (
            <label className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  clearError();
                }}
                className="mt-0.5 rounded border-slate-300 dark:border-slate-600"
                disabled={isLoading}
                required
              />
              <span>I agree to the terms and privacy policy.</span>
            </label>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
            aria-busy={isLoading}
          >
            {isLoading ? (
              <>
                <span className="sr-only">
                  {isSignup ? "Creating account" : "Signing in"}...
                </span>
                <span
                  className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"
                  aria-hidden="true"
                />
              </>
            ) : null}
            {isSignup ? "Create Account" : "Sign In"}
          </Button>
        </form>

        <div className="flex flex-col gap-2 text-center text-sm">
          <button
            onClick={toggleMode}
            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-sm"
            type="button"
            disabled={isLoading}
          >
            {isSignup
              ? "Already have an account? Sign in"
              : "Don't have an account? Sign up"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
